import type { SxProps, Theme } from "@mui/material";
import { useRef, useState } from "react";
import type { CalculationType } from "../../../../../../../../../api/types/fieldConfigurationTypes";
import useAvailableCalculationTypes from "../../hooks/useAvailableCalculationTypes";
import useShowDateCalculation from "../../hooks/useShowDateCalculation";
import CalculationMenuItem from "../custom-field/CalculationMenuItem";
import CustomCalculationOptionsMenu from "../custom-field/CustomCalculationOptionsMenu";
import { applicableToMeasureColumnCalculationTypes } from "../custom-field/utilities/calculationTypeCapabilities";
import type { DateCalculationType } from "../custom-field/utilities/dateCalculationTypes";

interface Props {
  onCalculationItemClick: (calculationType: CalculationType) => void;
  onDateCalculationItemClick?: (type: DateCalculationType) => void;
  menuItemSx?: SxProps<Theme>;
}

const CalculationFieldMenuItem = ({ onCalculationItemClick, onDateCalculationItemClick, menuItemSx }: Props) => {
  const availableCalculationTypeOptions = useAvailableCalculationTypes();
  const showDateCalculation = useShowDateCalculation();
  const [calculationMenuItemAnchor, setCalculationMenuItemAnchor] = useState<HTMLElement | null>(null);
  const customCalculationOptionsMenuRef = useRef<HTMLDivElement>(null);

  const handleCustomCalculationItemClick = (calculationType: CalculationType) => {
    onCalculationItemClick(calculationType);
  };

  const handleDateCalculationItemClick = (type: DateCalculationType) => {
    onDateCalculationItemClick?.(type);
  };

  const handleCalculationMenuItemClick = (target: HTMLElement) => {
    setCalculationMenuItemAnchor((prev) => (prev ? null : target));
  };

  const handleCalculationMenuItemClose = () => {
    setCalculationMenuItemAnchor(null);
  };

  const handleMouseLeave = (target: EventTarget | null) => {
    if (target instanceof Node && customCalculationOptionsMenuRef.current?.contains(target)) {
      return;
    }
    setCalculationMenuItemAnchor(null);
  };

  return (
    <>
      <CalculationMenuItem
        onMouseEnter={setCalculationMenuItemAnchor}
        onMouseLeave={handleMouseLeave}
        onClick={handleCalculationMenuItemClick}
        sx={menuItemSx}
      />
      <CustomCalculationOptionsMenu
        ref={customCalculationOptionsMenuRef}
        selectedCalculationType={undefined}
        calculationMenuItemAnchor={calculationMenuItemAnchor}
        onClose={handleCalculationMenuItemClose}
        onCustomCalculationItemClick={handleCustomCalculationItemClick}
        availableCalculationTypeOptions={applicableToMeasureColumnCalculationTypes.filter((type) =>
          availableCalculationTypeOptions.includes(type)
        )}
        onDateCalculationItemClick={
          showDateCalculation && onDateCalculationItemClick ? handleDateCalculationItemClick : undefined
        }
      />
    </>
  );
};

export default CalculationFieldMenuItem;
